import type { CSSProperties } from 'react';
import { NavIcon } from '../../shell/nav-icon';
import type { NavIconName } from '../../shell/nav-icon';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { monthName } from '@bookingdei/i18n/format-date';
import type {
  OverviewAnalytics,
  RevenueLineKey,
  SourceKey,
} from '../../mock/seeds/admin-overview-seed';
import type { Fmt } from './overview-model';
import { filledSegments, msg, num, pctChange } from './overview-model';

// The Overview's chart cards (design D:689-789 + `overviewVals` D:9136-9350): revenue
// mix, bookings over time, category / city split, booking sources, the health strip,
// plus the FR-A12 funnel the design dropped (kept by DR-39).

const LINE_META: Record<RevenueLineKey, { key: MessageKey; icon: NavIconName }> = {
  subscriptions: { key: 'admin.overview.rev.subscriptions', icon: 'card' },
  placement: { key: 'admin.overview.rev.placement', icon: 'layout' },
};

export function RevenueMixCard({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  const lines = a.revenue_lines ?? [];
  const total = lines.reduce((x, l) => x + l.month_minor, 0) || 1;
  return (
    <section className="ov-card" aria-labelledby="ov-rev-title">
      <h2 id="ov-rev-title" className="ov-card__title ov-card__title--sub">
        {f.t('admin.overview.rev.title')}
      </h2>
      <p className="ov-card__sub">{f.t('admin.overview.rev.sub')}</p>
      {lines.map((l) => {
        const meta = LINE_META[l.key];
        const p = pctChange(l.month_minor, l.prev_minor);
        const on = filledSegments((l.month_minor / total) * 100);
        return (
          <div key={l.key} className={`ov-rev ov-rev--${l.key}`}>
            <span className="ov-rev__icon" aria-hidden="true">
              <NavIcon name={meta?.icon ?? 'currency'} size={15} />
            </span>
            <div className="ov-rev__body">
              <div className="ov-rev__top">
                <span className="ov-rev__label">{meta ? f.t(meta.key) : l.key}</span>
                <span className="ov-rev__amount">{f.money(l.month_minor)}</span>
                {p !== null && (
                  <span className={p >= 0 ? 'ov-chip ov-chip--ok' : 'ov-chip ov-chip--danger'}>
                    {(p >= 0 ? '+' : '') + num(f, Math.round(p), 0)}%
                  </span>
                )}
              </div>
              <div className="ov-segs" aria-hidden="true">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className={i < on ? 'ov-seg ov-seg--on' : 'ov-seg'} />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

export function BookingsChartCard({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  const year = f.now.getFullYear();
  const current = f.now.getMonth();
  const byMonth = (metric: string) => {
    const m = new Map<number, number>();
    for (const it of a.items ?? []) {
      if (it.metric !== metric) continue;
      const d = new Date(`${it.day}T00:00:00`);
      if (d.getFullYear() === year) m.set(d.getMonth(), it.value);
    }
    return m;
  };
  const bookings = byMonth('confirmed_bookings');
  const subs = byMonth('paid_subscriptions');
  const max = Math.max(1, ...bookings.values(), ...subs.values());
  const h = (v: number) => `${Math.max(5, Math.round((v / max) * 118))}px`;
  return (
    <section className="ov-card" aria-labelledby="ov-bk-title">
      <h2 id="ov-bk-title" className="ov-card__title ov-card__title--sub">
        {f.t('admin.overview.bk.title')}
      </h2>
      <p className="ov-card__sub">{f.t('admin.overview.bk.sub')}</p>
      <div className="ov-legend">
        <span className="ov-legend__item">
          <span className="ov-legend__dot ov-legend__dot--s1" />
          {f.t('admin.overview.bk.bookings')}
        </span>
        <span className="ov-legend__item">
          <span className="ov-legend__dot ov-legend__dot--s2" />
          {f.t('admin.overview.bk.subscriptions')}
        </span>
      </div>
      <div className="ov-bars" role="img" aria-label={f.t('admin.overview.bk.title')}>
        {Array.from({ length: 12 }, (_, m) => {
          const v = bookings.get(m);
          const sv = subs.get(m);
          return (
            <div key={m} className="ov-bars__col">
              <div className="ov-bars__v">{v === undefined ? '' : num(f, v)}</div>
              <div className="ov-bars__pair">
                {v !== undefined && (
                  <div
                    className={m === current ? 'ov-bars__bar ov-bars__bar--now' : 'ov-bars__bar'}
                    style={{ height: `${Math.round((v / max) * 118)}px` }}
                  />
                )}
                {sv !== undefined && (
                  <div className="ov-bars__bar ov-bars__bar--sub" style={{ height: h(sv) }} />
                )}
              </div>
              <div className="ov-bars__sv">{sv === undefined ? '' : num(f, sv)}</div>
              <div className="ov-bars__m">{monthName(f.lang, m)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SplitCard({
  title,
  rows,
  min,
}: {
  title: string;
  rows: { label: string; pct: number; text: string }[];
  min: number;
}) {
  return (
    <section className="ov-card">
      <h2 className="ov-card__title">{title}</h2>
      <div className="ov-split">
        {rows.map((r) => (
          <div key={r.label} className="ov-split__row">
            <span className="ov-split__label">{r.label}</span>
            <div className="ov-split__track">
              <div className="ov-split__fill" style={{ width: `${Math.max(min, r.pct)}%` }} />
            </div>
            <span className="ov-split__pct">{r.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function SplitCards({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  const bn = f.lang === 'bn';
  return (
    <div className="ov-splits">
      <SplitCard
        title={f.t('admin.overview.category_split')}
        min={4}
        rows={(a.category_split ?? []).map((c) => ({
          label: bn ? c.label_bn : c.label_en,
          pct: c.pct,
          text: `${num(f, c.pct)}%`,
        }))}
      />
      <SplitCard
        title={f.t('admin.overview.city_split')}
        min={2.5}
        rows={(a.city_split ?? []).map((c) => ({
          label: bn ? c.name_bn : c.name_en,
          pct: c.pct,
          text: `${num(f, c.pct)}%`,
        }))}
      />
    </div>
  );
}

const SOURCE_KEY: Record<SourceKey, MessageKey> = {
  marketplace: 'admin.overview.src.marketplace',
  qr: 'admin.overview.src.qr',
  link: 'admin.overview.src.link',
  facebook: 'admin.overview.src.facebook',
};

export function SourcesCard({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  return (
    <section className="ov-card ov-card--src" aria-labelledby="ov-src-title">
      <h2 id="ov-src-title" className="ov-card__title ov-card__title--sub">
        {f.t('admin.overview.sources')}
      </h2>
      <p className="ov-card__sub ov-card__sub--tight">{f.t('admin.overview.sources_sub')}</p>
      <div className="ov-src">
        {(a.sources ?? []).map((s) => (
          <div key={s.key}>
            <div className="ov-src__top">
              <span className="ov-src__label">
                {SOURCE_KEY[s.key] ? f.t(SOURCE_KEY[s.key]) : s.key}
              </span>
              <span className="ov-src__pct">{num(f, s.pct)}%</span>
            </div>
            <div className="ov-src__track">
              <div
                className={`ov-src__fill ov-src__fill--${s.key}`}
                style={{ width: `${Math.max(s.pct, 4)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Display bands for the health strip (the contract carries the raw figures only).
const HEALTH_BANDS = {
  apiErrorPct: 1,
  smsDeliveryPct: 95,
  uptimePct: 99.9,
  searchP50Ms: 300,
} as const;

export function HealthStrip({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  const hl = a.health ?? {};
  const sms = a.sms_delivery_pct;
  const cells: {
    label: MessageKey;
    value: string;
    ok: boolean;
    tag: MessageKey;
    bad: MessageKey;
  }[] = [];
  if (typeof hl.api_error_rate === 'number')
    cells.push({
      label: 'admin.overview.health.api',
      value: `${num(f, hl.api_error_rate)}%`,
      ok: hl.api_error_rate <= HEALTH_BANDS.apiErrorPct,
      tag: 'admin.overview.health.healthy',
      bad: 'admin.overview.health.degraded',
    });
  if (typeof sms === 'number')
    cells.push({
      label: 'admin.overview.health.sms',
      value: `${num(f, sms)}%`,
      ok: sms >= HEALTH_BANDS.smsDeliveryPct,
      tag: 'admin.overview.health.healthy',
      bad: 'admin.overview.health.degraded',
    });
  if (typeof hl.uptime_30d === 'number')
    cells.push({
      label: 'admin.overview.health.uptime',
      value: `${num(f, hl.uptime_30d)}%`,
      ok: hl.uptime_30d >= HEALTH_BANDS.uptimePct,
      tag: 'admin.overview.health.healthy',
      bad: 'admin.overview.health.degraded',
    });
  if (typeof hl.search_p50_ms === 'number')
    cells.push({
      label: 'admin.overview.health.p50',
      value: msg(f, 'admin.overview.health.ms', { n: hl.search_p50_ms }),
      ok: hl.search_p50_ms <= HEALTH_BANDS.searchP50Ms,
      tag: 'admin.overview.health.fast',
      bad: 'admin.overview.health.slow',
    });
  return (
    <section className="ov-health" aria-label={f.t('admin.overview.health')}>
      {cells.map((c) => (
        <div key={c.label} className="ov-health__cell">
          <div className="ov-health__head">
            <span
              className={c.ok ? 'ov-health__dot' : 'ov-health__dot ov-health__dot--warn'}
              aria-hidden="true"
            />
            <span className="ov-health__label">{f.t(c.label)}</span>
          </div>
          <div className="ov-health__value">{c.value}</div>
          <div>
            <span className={c.ok ? 'ov-health__tag' : 'ov-health__tag ov-health__tag--warn'}>
              {f.t(c.ok ? c.tag : c.bad)}
            </span>
          </div>
        </div>
      ))}
    </section>
  );
}

export function FunnelCard({ a, f }: { a: OverviewAnalytics; f: Fmt }) {
  const rows = a.funnel ?? [];
  const max = Math.max(1, ...rows.map((r) => r.views));
  const w = (v: number): CSSProperties => ({ width: `${Math.max(1.5, (v / max) * 100)}%` });
  return (
    <section className="ov-card ov-card--funnel" aria-labelledby="ov-funnel-title">
      <h2 id="ov-funnel-title" className="ov-card__title ov-card__title--sub">
        {f.t('admin.overview.funnel')}
      </h2>
      <p className="ov-card__sub">{f.t('admin.overview.funnel_sub')}</p>
      <div className="ov-legend">
        <span className="ov-legend__item">
          <span className="ov-legend__dot ov-legend__dot--pale" />
          {f.t('admin.overview.legend.views')}
        </span>
        <span className="ov-legend__item">
          <span className="ov-legend__dot ov-legend__dot--s2" />
          {f.t('admin.overview.legend.bookings')}
        </span>
        <span className="ov-legend__item">
          <span className="ov-legend__dot ov-legend__dot--s1" />
          {f.t('admin.overview.legend.completions')}
        </span>
      </div>
      <div className="ov-funnel">
        {rows.map((r) => (
          <div key={r.key} className="ov-funnel__row">
            <span className="ov-funnel__label">{f.lang === 'bn' ? r.label_bn : r.label_en}</span>
            <div className="ov-funnel__bars" aria-hidden="true">
              <span className="ov-funnel__bar ov-funnel__bar--views" style={w(r.views)} />
              <span className="ov-funnel__bar ov-funnel__bar--bookings" style={w(r.bookings)} />
              <span className="ov-funnel__bar ov-funnel__bar--done" style={w(r.completions)} />
            </div>
            <span className="ov-funnel__nums">
              {msg(f, 'admin.overview.funnel_nums', {
                views: r.views,
                bookings: r.bookings,
                done: r.completions,
              })}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
